import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';

const STATUS_COLORS = {
  DRAFT: '#888', RUNNING: '#44BBA4', PAUSED: '#E8C547', COMPLETED: '#0F4C81',
};

function StatusPill({ status }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: '3px 10px', borderRadius: '999px', fontSize: '11.5px', fontWeight: 700,
      color: STATUS_COLORS[status] || '#888',
      background: `${STATUS_COLORS[status] || '#888'}18`,
      border: `1px solid ${STATUS_COLORS[status] || '#888'}40`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_COLORS[status] || '#888' }} />
      {status}
    </span>
  );
}

function NewCampaignModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', subject: '', senderDisplayName: 'Dwarkadhish Overseas',
    htmlBody: '', unsubscribeEmail: '', delayMinSec: 4, delayMaxSec: 9, senderIds: [],
  });
  const [htmlFile, setHtmlFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(true);
  const [testTo, setTestTo] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testMsg, setTestMsg] = useState('');
  const [senders, setSenders] = useState([]);

  useEffect(() => { api.senders.list().then(setSenders).catch(() => {}); }, []);

  const toggleSender = (id) => {
    setForm(f => ({
      ...f,
      senderIds: f.senderIds.includes(id) ? f.senderIds.filter(x => x !== id) : [...f.senderIds, id],
    }));
  };

  const handleHtmlFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setHtmlFile(file);
    const reader = new FileReader();
    reader.onload = ev => setForm(f => ({ ...f, htmlBody: ev.target.result }));
    reader.readAsText(file);
  };

  const submit = async () => {
    if (!form.name || !form.subject || !form.htmlBody) {
      setError('Name, subject and an HTML template are all required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { id } = await api.campaigns.create(form);
      onCreated(id);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testTo.trim()) { setTestMsg('Enter an address to send the test to.'); return; }
    if (!form.subject || !form.htmlBody) { setTestMsg('Add a subject and HTML template first.'); return; }
    setTestSending(true);
    setTestMsg('');
    try {
      const r = await api.campaigns.testSend({
        to: testTo.trim(),
        subject: form.subject,
        htmlBody: form.htmlBody,
        senderDisplayName: form.senderDisplayName,
        unsubscribeEmail: form.unsubscribeEmail,
        senderId: form.senderIds[0],
      });
      setTestMsg(`Test sent from ${r.sentFrom}.`);
    } catch (e) {
      setTestMsg(e.message);
    } finally {
      setTestSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '640px', width: '94vw' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>New Email Campaign</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div>
            <label className="field-label">Campaign Name</label>
            <input className="field-input" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. RoDTEP Refund Audit — August" />
          </div>
          <div>
            <label className="field-label">Subject Line</label>
            <input className="field-input" value={form.subject}
              onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
              placeholder="Pending export Refunds?" />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label className="field-label">From Name</label>
              <input className="field-input" value={form.senderDisplayName}
                onChange={e => setForm(f => ({ ...f, senderDisplayName: e.target.value }))} />
            </div>
            <div style={{ flex: 1 }}>
              <label className="field-label">Unsubscribe Contact (optional)</label>
              <input className="field-input" value={form.unsubscribeEmail}
                onChange={e => setForm(f => ({ ...f, unsubscribeEmail: e.target.value }))}
                placeholder="info@dwarkadhishoverseas.com" />
            </div>
          </div>
          <div>
            <label className="field-label">Senders to use for this campaign</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid rgba(15,60,96,0.15)', borderRadius: 8, padding: 10, maxHeight: 160, overflowY: 'auto' }}>
              {senders.length === 0 && (
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>No senders yet — add one in the Sender Pool below first.</span>
              )}
              {senders.map(s => (
                <label key={s.ID} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <input type="checkbox" checked={form.senderIds.includes(s.ID)} onChange={() => toggleSender(s.ID)} />
                  <span>{s.EMAIL}</span>
                  <span style={{ color: 'var(--text-3)', fontSize: 12 }}>(cap {s.DAILY_CAP}/day{s.ACTIVE !== 'YES' ? ', inactive' : ''})</span>
                </label>
              ))}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
              {form.senderIds.length === 0
                ? 'None selected — sending will rotate across every active sender in the pool.'
                : `${form.senderIds.length} sender(s) selected for this campaign.`}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label className="field-label">Min delay between sends (sec)</label>
              <input className="field-input" type="number" min={1} value={form.delayMinSec}
                onChange={e => setForm(f => ({ ...f, delayMinSec: Number(e.target.value) }))} />
            </div>
            <div style={{ flex: 1 }}>
              <label className="field-label">Max delay between sends (sec)</label>
              <input className="field-input" type="number" min={1} value={form.delayMaxSec}
                onChange={e => setForm(f => ({ ...f, delayMaxSec: Number(e.target.value) }))} />
            </div>
          </div>
          <div>
            <label className="field-label">HTML Template</label>
            <input type="file" accept=".html,.htm" onChange={handleHtmlFile} style={{ marginBottom: 8 }} />
            <textarea className="field-input" rows={8} value={form.htmlBody}
              onChange={e => setForm(f => ({ ...f, htmlBody: e.target.value }))}
              placeholder="Paste your HTML email body here, or upload an .html file above."
              style={{ fontFamily: 'monospace', fontSize: '12px' }} />
            {htmlFile && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>Loaded: {htmlFile.name}</div>}
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label className="field-label" style={{ margin: 0 }}>Preview</label>
              <button type="button" className="page-btn" onClick={() => setShowPreview(s => !s)}>
                {showPreview ? 'Hide preview' : 'Show preview'}
              </button>
            </div>
            {showPreview && (
              form.htmlBody
                ? (
                  <iframe
                    title="Email preview"
                    srcDoc={form.htmlBody}
                    sandbox=""
                    style={{ width: '100%', height: 320, border: '1px solid rgba(15,60,96,0.15)', borderRadius: 8, marginTop: 8, background: '#fff' }}
                  />
                )
                : <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 8 }}>Nothing to preview yet — paste or upload an HTML template above.</div>
            )}
          </div>

          <div>
            <label className="field-label">Send yourself a test email first</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="field-input" style={{ flex: 1 }} placeholder="you@example.com"
                value={testTo} onChange={e => setTestTo(e.target.value)} />
              <button type="button" className="btn-primary" disabled={testSending} onClick={sendTestEmail}>
                {testSending ? 'Sending…' : 'Send Test'}
              </button>
            </div>
            {testMsg && <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 6 }}>{testMsg}</div>}
          </div>
          {error && <div className="page-error" style={{ margin: 0 }}>{error}</div>}
          <div className="modal-actions">
            <button className="btn-primary" disabled={saving} onClick={submit}>
              {saving ? 'Creating…' : 'Create Campaign'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SendersPanel() {
  const [senders, setSenders] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: '', appPassword: '', displayName: '', dailyCap: '' });
  const [error, setError] = useState('');

  const load = useCallback(() => { api.senders.list().then(setSenders).catch(e => setError(e.message)); }, []);
  useEffect(load, [load]);

  const addSender = async () => {
    if (!form.email || !form.appPassword) { setError('Email and Gmail app password are required.'); return; }
    try {
      await api.senders.create(form);
      setForm({ email: '', appPassword: '', displayName: '', dailyCap: '' });
      setShowAdd(false);
      setError('');
      load();
    } catch (e) { setError(e.message); }
  };

  const toggleActive = async (s) => {
    await api.senders.update(s.ID, { active: s.ACTIVE !== 'YES' });
    load();
  };

  const updateCap = async (s, value) => {
    const cap = Number(value);
    if (!Number.isFinite(cap) || cap <= 0) return;
    await api.senders.update(s.ID, { dailyCap: cap });
    load();
  };

  const removeSender = async (s) => {
    if (!window.confirm(`Remove sender ${s.EMAIL}?`)) return;
    await api.senders.remove(s.ID);
    load();
  };

  return (
    <div className="table-card" style={{ marginTop: 24 }}>
      <div className="table-toolbar">
        <div className="page-title" style={{ fontSize: 16 }}>Sender Pool (Gmail)</div>
        <button className="btn-primary" style={{ marginLeft: 'auto' }} onClick={() => setShowAdd(s => !s)}>
          {showAdd ? 'Cancel' : '+ Add Sender'}
        </button>
      </div>

      {showAdd && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '0 20px 16px' }}>
          <input className="field-input" style={{ flex: '1 1 200px' }} placeholder="sender@gmail.com"
            value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          <input className="field-input" style={{ flex: '1 1 200px' }} placeholder="16-char Gmail App Password" type="password"
            value={form.appPassword} onChange={e => setForm(f => ({ ...f, appPassword: e.target.value }))} />
          <input className="field-input" style={{ flex: '1 1 160px' }} placeholder="Display name (optional)"
            value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} />
          <input className="field-input" style={{ width: 130 }} type="number" placeholder="Daily cap (e.g. 1800)"
            value={form.dailyCap} onChange={e => setForm(f => ({ ...f, dailyCap: e.target.value === '' ? '' : Number(e.target.value) }))} />
          <button className="btn-primary" onClick={addSender}>Save</button>
        </div>
      )}
      {error && <div className="page-error" style={{ margin: '0 20px 16px' }}>{error}</div>}

      <div className="table-scroll">
        <table className="data-table">
          <thead><tr>
            <th>Email</th><th>Sent Today</th><th>Daily Cap</th><th>Status</th><th>Active</th><th></th>
          </tr></thead>
          <tbody>
            {senders.map(s => (
              <tr key={s.ID}>
                <td>{s.EMAIL}</td>
                <td>{s.SENT_TODAY}</td>
                <td>
                  <input className="field-input" type="number" defaultValue={s.DAILY_CAP} style={{ width: 90 }}
                    onBlur={e => { if (Number(e.target.value) !== s.DAILY_CAP) updateCap(s, e.target.value); }} />
                </td>
                <td>{s.MAXED === 'YES'
                  ? <span className="inc-badge inc-badge--fail">Maxed / cooling down</span>
                  : <span className="inc-badge inc-badge--success">Ready</span>}</td>
                <td>
                  <button className="page-btn" onClick={() => toggleActive(s)}>
                    {s.ACTIVE === 'YES' ? 'Active ✓' : 'Inactive'}
                  </button>
                </td>
                <td><button className="btn-danger" onClick={() => removeSender(s)}>Remove</button></td>
              </tr>
            ))}
            {senders.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 20, color: 'var(--text-3)' }}>
                No senders yet — add at least one Gmail address (with an App Password, not your normal password) to send campaigns.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function EmailCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    api.campaigns.list().then(setCampaigns).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Email Campaigns</div>
          <div className="page-desc">Create, send and monitor bulk outreach — sends rotate across the senders you pick for each campaign (or the whole pool) with pacing and automatic retry.</div>
        </div>
        <button className="btn-primary" onClick={() => setShowNew(true)}>+ New Campaign</button>
      </div>

      <div className="table-card">
        {loading ? (
          <div className="table-loading"><div className="spinner" /></div>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead><tr>
                <th>Campaign</th><th>Subject</th><th>Status</th><th>Sent</th><th>Failed</th><th>Recipients</th><th>Created</th>
              </tr></thead>
              <tbody>
                {campaigns.map(c => (
                  <tr key={c.ID} style={{ cursor: 'pointer' }} onClick={() => navigate(`/campaigns/${c.ID}`)}>
                    <td style={{ fontWeight: 600 }}>{c.NAME}</td>
                    <td>{c.SUBJECT}</td>
                    <td><StatusPill status={c.STATUS} /></td>
                    <td>{c.SENT_COUNT}</td>
                    <td>{c.FAILED_COUNT}</td>
                    <td>{c.TOTAL_RECIPIENTS}</td>
                    <td>{c.CREATED_AT ? new Date(c.CREATED_AT).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
                {campaigns.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 20, color: 'var(--text-3)' }}>
                    No campaigns yet. Click "New Campaign" to create one.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <SendersPanel />

      {showNew && (
        <NewCampaignModal
          onClose={() => setShowNew(false)}
          onCreated={(id) => { setShowNew(false); navigate(`/campaigns/${id}`); }}
        />
      )}
    </div>
  );
}
